import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import {
  PdfMarkupActionTypes,
  LoadMarkups,
  LoadMarkupsSuccess,
  LoadMarkupsFailure,
  SaveMarkups,
  SaveMarkupsSuccess,
  SaveMarkupsFailure,
  DeleteMarkup,
  DeleteMarkupSuccess,
  DeleteMarkupFailure,
} from '@dwo/actions';
import { State } from '@dwo/reducers';
import { PdfMarkupService } from '@app/core/services/pdf-markup.service';

const errorMessage = (err: unknown): string =>
  (err as { message?: string })?.message ?? 'Unknown error while communicating with the server.';

@Injectable()
export class PdfMarkupEffect {
  constructor(
    readonly actions$: Actions,
    readonly markupService: PdfMarkupService,
    readonly store: Store<State>,
  ) {}

  loadMarkups$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadMarkups>(PdfMarkupActionTypes.LoadMarkups),
        mergeMap((action) =>
          this.markupService.loadMarkups(action.documentId).pipe(
            map((annotationsByPage) => new LoadMarkupsSuccess(action.documentId, annotationsByPage)),
            catchError((err) => of(new LoadMarkupsFailure(action.documentId, errorMessage(err)))),
          ),
        ),
      ),
  );

  saveMarkups$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<SaveMarkups>(PdfMarkupActionTypes.SaveMarkups),
        switchMap((action) =>
          this.markupService.saveMarkups(action.documentId, action.annotationsByPage).pipe(
            map((annotationsByPage) => new SaveMarkupsSuccess(action.documentId, annotationsByPage)),
            catchError((err) => of(new SaveMarkupsFailure(action.documentId, errorMessage(err)))),
          ),
        ),
      ),
  );

  deleteMarkup$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<DeleteMarkup>(PdfMarkupActionTypes.DeleteMarkup),
        mergeMap((action) =>
          this.markupService.deleteMarkup(action.documentId, action.markupId).pipe(
            map(() => new DeleteMarkupSuccess(action.documentId, action.markupId)),
            catchError((err) => of(new DeleteMarkupFailure(action.documentId, action.markupId, errorMessage(err)))),
          ),
        ),
      ),
  );
}
